import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { canEnterApp } from '@helios/authz';
import { useAuth } from '@helios/auth-firebase-web';
import { useSendWhatsAppBookingFlow } from '../hooks/useSendWhatsAppBookingFlow';

/* ------------------------------------------------------------------ */
/*  SendWhatsAppBookingFlowAction (PRD.258)                          */
/*                                                                    */
/*  Sends the approved booking-entry template (Flow button) to a      */
/*  patient so they can self-book inside WhatsApp. Creates the Flow   */
/*  session + flow_token server-side before sending. Uses an approved */
/*  template, so it works outside the 24-hour window.                 */
/*                                                                    */
/*  - Role-gated (admin/editor).                                      */
/*  - Disabled when no mobile number is on file or endpoint unset.    */
/* ------------------------------------------------------------------ */

const ALLOWED_ROLES = ['admin', 'editor'] as const;

export interface SendWhatsAppBookingFlowActionProps {
  /** Patient context (patientId optional — stored for prefill when known). */
  patient: { id?: string; name: string };
  /** Patient's mobile number (E.164 or raw). Required to send. */
  patientPhone: string | null;
  presentation?: 'menu-item' | 'button';
  /** Stretch the button to fill its container (matches full-width CTAs). */
  fullWidth?: boolean;
}

export function SendWhatsAppBookingFlowAction({
  patient,
  patientPhone,
  presentation = 'button',
  fullWidth = false,
}: SendWhatsAppBookingFlowActionProps) {
  const { claims } = useAuth();
  const { enabled, status, error, send, reset } = useSendWhatsAppBookingFlow();

  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canSend = canEnterApp(claims, {
    appId: 'whatsapp-booking-flow',
    entryAnyOfRoles: [...ALLOWED_ROLES],
  });

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const blockReason: string | null = !enabled
    ? 'Booking messaging is not configured for this clinic'
    : !patientPhone
      ? 'No mobile number on file for this patient'
      : null;
  const disabled = blockReason !== null;

  const openDialog = useCallback(() => {
    if (disabled) return;
    reset();
    setOpen(true);
  }, [disabled, reset]);

  const handleSend = useCallback(async () => {
    if (status === 'sending' || !patientPhone) return; // double-click / guard
    const result = await send({
      to: patientPhone,
      patientId: patient.id,
      clientReference: patient.id ? `patient:${patient.id}:booking-flow` : undefined,
    });
    if (result) {
      setOpen(false);
      showToast(`Booking message sent to ${patient.name}.`);
    }
  }, [status, patientPhone, send, patient.id, patient.name, showToast]);

  if (!canSend) return null;

  return (
    <>
      {presentation === 'button' ? (
        <Button
          variant="outline"
          size="md"
          onClick={openDialog}
          disabled={disabled}
          title={blockReason ?? undefined}
          style={fullWidth ? { width: '100%' } : undefined}
        >
          Send booking link
        </Button>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          disabled={disabled}
          title={blockReason ?? undefined}
          style={{
            ...menuItemStyle,
            color: disabled ? 'var(--text-muted)' : 'var(--text)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          Send booking link
        </button>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Send WhatsApp booking link"
        description="Sends the approved booking menu so the patient can pick a doctor, date, and time inside WhatsApp."
        maxWidth={440}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleSend}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending…' : 'Send booking link'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <div style={summaryBoxStyle}>
            <SummaryRow label="Patient" value={patient.name} />
            <SummaryRow label="WhatsApp" value={patientPhone ?? '—'} />
          </div>
          {status === 'error' && error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      </Modal>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6)',
            right: 'var(--sp-6)',
            zIndex: 1000,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

const modalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
  padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
};

const summaryBoxStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3) var(--sp-4)',
  background: 'var(--bg-surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
};

const errorBoxStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: '#fef2f2',
  color: '#dc2626',
  fontSize: 'var(--font-size-sm)',
};

const menuItemStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'none',
  border: 'none',
  textAlign: 'left',
  fontSize: 'var(--font-size-sm)',
};
